import React, { useState, useRef, useEffect } from "react";
import {
  FaVolumeUp,
  FaVolumeMute,
  FaRedo,
  FaPalette,
  FaCheckCircle,
  FaPencilAlt,
  FaStar,
  FaMicrophone,
  FaUser,
  FaSmile
} from "react-icons/fa";

/**
 * Cambridge-Style Capital Alphabet Cues & Guides (A-Z)
 * Uses normalized SVG vector path strings for smooth, gapless geometry,
 * plus vocabulary examples for the new left-side panel.
 */
const ALPHABET_DATA = {
  A: {
    label: "A",
    exampleWord: "Apple",
    exampleEmoji: "🍎",
    rhyme: "Slant up, slant down, and draw a line across — that makes an A!",
    paths: [
      "M 0.50 0.18 L 0.30 0.82",
      "M 0.50 0.18 L 0.70 0.82",
      "M 0.38 0.56 L 0.62 0.56"
    ]
  },
  B: {
    label: "B",
    exampleWord: "Ball",
    exampleEmoji: "⚽",
    rhyme: "Straight line down, a bump on top, a bump on bottom — that makes a B!",
    paths: [
      "M 0.35 0.18 L 0.35 0.82",
      "M 0.35 0.18 C 0.68 0.18, 0.68 0.50, 0.35 0.50",
      "M 0.35 0.50 C 0.72 0.50, 0.72 0.82, 0.35 0.82"
    ]
  },
  C: {
    label: "C",
    exampleWord: "Cat",
    exampleEmoji: "🐱",
    rhyme: "Curve around from top to bottom — that makes a C!",
    paths: ["M 0.68 0.30 C 0.32 0.15, 0.32 0.85, 0.68 0.70"]
  },
  D: {
    label: "D",
    exampleWord: "Dog",
    exampleEmoji: "🐶",
    rhyme: "Straight line down, then one big curve — that makes a D!",
    paths: ["M 0.35 0.18 L 0.35 0.82", "M 0.35 0.18 C 0.78 0.18, 0.78 0.82, 0.35 0.82"]
  },
  E: {
    label: "E",
    exampleWord: "Elephant",
    exampleEmoji: "🐘",
    rhyme: "Straight line down, then three lines across — that makes an E!",
    paths: [
      "M 0.35 0.18 L 0.35 0.82",
      "M 0.35 0.18 L 0.68 0.18",
      "M 0.35 0.50 L 0.62 0.50",
      "M 0.35 0.82 L 0.68 0.82"
    ]
  },
  F: {
    label: "F",
    exampleWord: "Fish",
    exampleEmoji: "🐟",
    rhyme: "Straight line down, hat on top, middle branch — that makes an F!",
    paths: [
      "M 0.35 0.18 L 0.35 0.82",
      "M 0.35 0.18 L 0.68 0.18",
      "M 0.35 0.50 L 0.62 0.50"
    ]
  },
  G: {
    label: "G",
    exampleWord: "Grape",
    exampleEmoji: "🍇",
    rhyme: "Curve around like C, then bring a little shelf in — that makes a G!",
    paths: [
      "M 0.68 0.30 C 0.30 0.15, 0.30 0.85, 0.68 0.70",
      "M 0.68 0.70 L 0.68 0.52 L 0.50 0.52"
    ]
  },
  H: {
    label: "H",
    exampleWord: "Hat",
    exampleEmoji: "🎩",
    rhyme: "Two tall pillars, connected in the middle — that makes an H!",
    paths: [
      "M 0.32 0.18 L 0.32 0.82",
      "M 0.68 0.18 L 0.68 0.82",
      "M 0.32 0.50 L 0.68 0.50"
    ]
  },
  I: {
    label: "I",
    exampleWord: "Ice Cream",
    exampleEmoji: "🍦",
    rhyme: "A straight line down with top and bottom hats — that makes an I!",
    paths: [
      "M 0.5 0.22 L 0.5 0.78",
      "M 0.35 0.22 L 0.65 0.22",
      "M 0.35 0.78 L 0.65 0.78"
    ]
  },
  J: {
    label: "J",
    exampleWord: "Juice",
    exampleEmoji: "🧃",
    rhyme: "Go down and hook to the left — that makes a J!",
    paths: [
      "M 0.35 0.20 L 0.62 0.20",
      "M 0.58 0.20 L 0.58 0.68 C 0.58 0.86, 0.36 0.86, 0.34 0.72"
    ]
  },
  K: {
    label: "K",
    exampleWord: "Kite",
    exampleEmoji: "🪁",
    rhyme: "Straight line down, kick up, kick down — that makes a K!",
    paths: [
      "M 0.35 0.18 L 0.35 0.82",
      "M 0.65 0.22 L 0.35 0.50 L 0.65 0.82"
    ]
  },
  L: {
    label: "L",
    exampleWord: "Lion",
    exampleEmoji: "🦁",
    rhyme: "Straight line down, slide to the right — that makes an L!",
    paths: ["M 0.36 0.18 L 0.36 0.82 L 0.68 0.82"]
  },
  M: {
    label: "M",
    exampleWord: "Monkey",
    exampleEmoji: "🐵",
    rhyme: "Straight up, slant down, slant up, straight down — that makes an M!",
    paths: [
      "M 0.28 0.82 L 0.28 0.18 L 0.50 0.58 L 0.72 0.18 L 0.72 0.82"
    ]
  },
  N: {
    label: "N",
    exampleWord: "Nest",
    exampleEmoji: "🪺",
    rhyme: "Straight line up, slant across, straight line up — that makes an N!",
    paths: ["M 0.32 0.82 L 0.32 0.18 L 0.68 0.82 L 0.68 0.18"]
  },
  O: {
    label: "O",
    exampleWord: "Orange",
    exampleEmoji: "🍊",
    rhyme: "Round and round, back to the top — that makes an O!",
    paths: [
      "M 0.5 0.18 C 0.25 0.18, 0.25 0.82, 0.5 0.82 C 0.75 0.82, 0.75 0.18, 0.5 0.18"
    ]
  },
  P: {
    label: "P",
    exampleWord: "Penguin",
    exampleEmoji: "🐧",
    rhyme: "Straight line down, one bump at the top — that makes a P!",
    paths: [
      "M 0.35 0.18 L 0.35 0.82",
      "M 0.35 0.18 C 0.70 0.18, 0.70 0.52, 0.35 0.52"
    ]
  },
  Q: {
    label: "Q",
    exampleWord: "Queen",
    exampleEmoji: "👑",
    rhyme: "Make a big round O, then give it a tail — that makes a Q!",
    paths: [
      "M 0.5 0.18 C 0.30 0.18, 0.30 0.82, 0.5 0.82 C 0.70 0.82, 0.70 0.18, 0.5 0.18",
      "M 0.55 0.62 L 0.70 0.82"
    ]
  },
  R: {
    label: "R",
    exampleWord: "Robot",
    exampleEmoji: "🤖",
    rhyme: "Straight line down, bump at top, kick down a leg — that makes an R!",
    paths: [
      "M 0.35 0.18 L 0.35 0.82",
      "M 0.35 0.18 C 0.75 0.18, 0.75 0.50, 0.35 0.50",
      "M 0.35 0.50 L 0.68 0.82"
    ]
  },
  S: {
    label: "S",
    exampleWord: "Sun",
    exampleEmoji: "☀️",
    rhyme: "Curve to the left, curve to the right — that makes an S!",
    paths: [
      "M 0.64 0.28 C 0.60 0.16, 0.34 0.16, 0.34 0.36 C 0.34 0.48, 0.45 0.49, 0.50 0.50 C 0.55 0.51, 0.66 0.52, 0.66 0.64 C 0.66 0.84, 0.40 0.84, 0.36 0.72"
    ]
  },
  T: {
    label: "T",
    exampleWord: "Tree",
    exampleEmoji: "🌳",
    rhyme: "Cross on top, straight down from the middle — that makes a T!",
    paths: ["M 0.30 0.18 L 0.70 0.18", "M 0.50 0.18 L 0.50 0.82"]
  },
  U: {
    label: "U",
    exampleWord: "Umbrella",
    exampleEmoji: "☂️",
    rhyme: "Swing down, curve around, up to the top — that makes a U!",
    paths: ["M 0.32 0.18 L 0.32 0.62 C 0.32 0.85, 0.68 0.85, 0.68 0.62 L 0.68 0.18"]
  },
  V: {
    label: "V",
    exampleWord: "Violin",
    exampleEmoji: "🎻",
    rhyme: "Slant down, slant up — that makes a V!",
    paths: ["M 0.28 0.18 L 0.50 0.82 L 0.72 0.18"]
  },
  W: {
    label: "W",
    exampleWord: "Whale",
    exampleEmoji: "🐳",
    rhyme: "Slant down, slant up, slant down, slant up — that makes a W!",
    paths: [
      "M 0.26 0.18 L 0.38 0.82 L 0.50 0.22 L 0.62 0.82 L 0.74 0.18"
    ]
  },
  X: {
    label: "X",
    exampleWord: "Xylophone",
    exampleEmoji: "🎹",
    rhyme: "Slant right, slant left, cross in the middle — that makes an X!",
    paths: ["M 0.30 0.18 L 0.70 0.82", "M 0.70 0.18 L 0.30 0.82"]
  },
  Y: {
    label: "Y",
    exampleWord: "Yacht",
    exampleEmoji: "⛵",
    rhyme: "Make a V at the top, then add a stem down — that makes a Y!",
    paths: ["M 0.30 0.18 L 0.50 0.48 L 0.70 0.18", "M 0.50 0.48 L 0.50 0.82"]
  },
  Z: {
    label: "Z",
    exampleWord: "Zebra",
    exampleEmoji: "🦓",
    rhyme: "Across to the right, slant down left, across to the right — that makes a Z!",
    paths: ["M 0.30 0.18 L 0.70 0.18 L 0.30 0.82 L 0.70 0.82"]
  }
};

/**
 * Helper function to parse and draw normalized SVG paths on HTML5 Canvas
 */
const drawNormalizedPath = (ctx, pathStr, width, height) => {
  const tokens = pathStr.trim().split(/\s+/);
  ctx.beginPath();
  let i = 0;

  while (i < tokens.length) {
    const cmd = tokens[i];
    if (cmd === "M") {
      ctx.moveTo(parseFloat(tokens[i + 1]) * width, parseFloat(tokens[i + 2]) * height);
      i += 3;
    } else if (cmd === "L") {
      ctx.lineTo(parseFloat(tokens[i + 1]) * width, parseFloat(tokens[i + 2]) * height);
      i += 3;
    } else if (cmd === "C") {
      ctx.bezierCurveTo(
        parseFloat(tokens[i + 1]) * width,
        parseFloat(tokens[i + 2]) * height,
        parseFloat(tokens[i + 3]) * width,
        parseFloat(tokens[i + 4]) * height,
        parseFloat(tokens[i + 5]) * width,
        parseFloat(tokens[i + 6]) * height
      );
      i += 7;
    } else {
      i++;
    }
  }
  ctx.stroke();
};

export default function AlphabetTracingGame() {
  const canvasRef = useRef(null);
  const [selectedLetter, setSelectedLetter] = useState("A");
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#2563EB");
  const [brushSize, setBrushSize] = useState(14);
  const [aiSpeech, setAiSpeech] = useState("");
  const [tracingResult, setTracingResult] = useState(null);
  const [isMuted, setIsMuted] = useState(false);

  // Name State
  const [userName, setUserName] = useState("Little Star");
  const [inputName, setInputName] = useState("");
  const [showModal, setShowModal] = useState(true);
  const [isListening, setIsListening] = useState(false);

  const colors = [
    { name: "Royal Blue", hex: "#2563EB" },
    { name: "Ruby Red", hex: "#DC2626" },
    { name: "Emerald Green", hex: "#16A34A" },
    { name: "Sunny Yellow", hex: "#D97706" },
    { name: "Berry Purple", hex: "#9333EA" }
  ];

  const alphabetList = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  const listenForName = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Voice recognition isn't supported on this browser, but you can type your name!");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);

    recognition.onresult = (event) => {
      const spokenName = event.results[0][0].transcript.replace(/\.$/, "");
      if (spokenName) {
        setInputName(spokenName);
      }
    };

    recognition.start();
  };

  const handleSaveName = (nameToSave) => {
    const finalName = nameToSave && nameToSave.trim() !== "" ? nameToSave.trim() : "Little Star";
    setUserName(finalName);
    setShowModal(false);
    triggerInitialGreeting(finalName);
  };

  const triggerInitialGreeting = (name) => {
    const currentInfo = ALPHABET_DATA[selectedLetter];
    const text = `Hello ${name}! Let's trace the letter ${selectedLetter}! ${currentInfo.exampleWord} starts with ${selectedLetter}. ${currentInfo.rhyme}`;
    setAiSpeech(text);
    if (!isMuted) speakAi(text);
  };

  useEffect(() => {
    drawGuideCanvas();
    if (!showModal) {
      const currentInfo = ALPHABET_DATA[selectedLetter];
      const text = `Let's trace the letter ${selectedLetter}, ${userName}! Like ${currentInfo.exampleWord}. ${currentInfo.rhyme}`;
      setAiSpeech(text);
      if (!isMuted) {
        speakAi(text);
      }
    }
    setTracingResult(null);
  }, [selectedLetter, showModal]);

  const speakAi = (text) => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      if (isMuted) return;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.pitch = 1.2;
      utterance.rate = 0.85;
      window.speechSynthesis.speak(utterance);
    }
  };

  const toggleMute = () => {
    const nextMuteState = !isMuted;
    setIsMuted(nextMuteState);
    if (nextMuteState) {
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    } else {
      speakAi(aiSpeech);
    }
  };

  const drawGuideCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = canvas.offsetWidth || 500;
    canvas.height = canvas.offsetHeight || 380;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.lineWidth = 8;
    ctx.strokeStyle = "#CBD5E1";
    ctx.setLineDash([12, 12]);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    const paths = ALPHABET_DATA[selectedLetter].paths;
    const w = canvas.width;
    const h = canvas.height;

    paths.forEach((pathStr) => {
      drawNormalizedPath(ctx, pathStr, w, h);
    });

    ctx.setLineDash([]);
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    ctx.beginPath();
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
    ctx.strokeStyle = selectedColor;
    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
    }
  };

  const resetCanvas = () => {
    drawGuideCanvas();
    setTracingResult(null);
    const currentInfo = ALPHABET_DATA[selectedLetter];
    const text = `Let's try letter ${selectedLetter} again, ${userName}! ${currentInfo.rhyme}`;
    setAiSpeech(text);
    if (!isMuted) speakAi(text);
  };

  const calculateAccuracy = () => {
    const canvas = canvasRef.current;
    if (!canvas) return { percent: 0, reason: "empty" };

    const w = canvas.width;
    const h = canvas.height;
    const userCtx = canvas.getContext("2d");
    const userData = userCtx.getImageData(0, 0, w, h).data;

    const guideCanvas = document.createElement("canvas");
    guideCanvas.width = w;
    guideCanvas.height = h;
    const guideCtx = guideCanvas.getContext("2d");

    guideCtx.fillStyle = "#FFFFFF";
    guideCtx.fillRect(0, 0, w, h);
    guideCtx.lineWidth = 32;
    guideCtx.strokeStyle = "#000000";
    guideCtx.lineCap = "round";
    guideCtx.lineJoin = "round";

    const paths = ALPHABET_DATA[selectedLetter].paths;
    paths.forEach((pathStr) => {
      drawNormalizedPath(guideCtx, pathStr, w, h);
    });

    const guideData = guideCtx.getImageData(0, 0, w, h).data;

    let targetPixels = 0;
    let coveredTargetPixels = 0;
    let totalDrawnPixels = 0;
    let strayPixels = 0;

    for (let i = 0; i < userData.length; i += 16) {
      const r = userData[i];
      const g = userData[i + 1];
      const b = userData[i + 2];

      const isUserDrawn =
        !(r > 240 && g > 240 && b > 240) &&
        !(r > 190 && r < 215 && g > 200 && b > 220);

      const isTargetArea = guideData[i] < 50;

      if (isTargetArea) {
        targetPixels++;
        if (isUserDrawn) coveredTargetPixels++;
      }

      if (isUserDrawn) {
        totalDrawnPixels++;
        if (!isTargetArea) strayPixels++;
      }
    }

    if (totalDrawnPixels < 30) {
      return { percent: 0, reason: "empty" };
    }

    const coverageRatio = coveredTargetPixels / (targetPixels || 1);
    const precisionRatio = 1 - strayPixels / totalDrawnPixels;

    let finalPercent = Math.round((coverageRatio * 0.7 + precisionRatio * 0.3) * 100);
    finalPercent = Math.min(100, Math.max(0, finalPercent));

    return { percent: finalPercent, reason: "ok" };
  };

  const markComplete = () => {
    const { percent, reason } = calculateAccuracy();

    if (reason === "empty") {
      const text = `Try tracing the letter ${selectedLetter} before checking, ${userName}!`;
      setAiSpeech(text);
      if (!isMuted) speakAi(text);
      setTracingResult(null);
      return;
    }

    let praise = "";
    let tier = "";

    if (percent >= 90) {
      tier = "Incredible!";
      praise = `Incredible ${userName}! ${percent}% accuracy! You are a letter tracing superstar!`;
    } else if (percent >= 75) {
      tier = "Excellent!";
      praise = `Excellent job, ${userName}! ${percent}% accuracy! Beautiful handwriting!`;
    } else if (percent >= 55) {
      tier = "Good Job!";
      praise = `Good Job ${userName}! ${percent}% accuracy! Keep practicing letter ${selectedLetter}!`;
    } else if (percent >= 40) {
      tier = "Nice Try!";
      praise = `Nice try ${userName}! ${percent}% accuracy. Let's try staying closer to the lines!`;
    } else {
      tier = "Keep Practicing";
      praise = `Let's try again, ${userName}! Trace right over the dotted lines for ${selectedLetter}.`;
    }

    setTracingResult({ percent, label: tier });
    setAiSpeech(praise);
    if (!isMuted) speakAi(praise);
  };

  const currentInfo = ALPHABET_DATA[selectedLetter];

  return (
    <div style={styles.container}>
      {/* Interactive Modal asking for Name on Load */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.modalHeader}>
              <FaSmile size={42} color="#2563EB" />
              <h2 style={styles.modalTitle}>Welcome Little Learner!</h2>
              <p style={styles.modalSubtitle}>What is your name today?</p>
            </div>

            <div style={styles.modalBody}>
              <button
                type="button"
                onClick={listenForName}
                style={{
                  ...styles.micPromptBtn,
                  backgroundColor: isListening ? "#EF4444" : "#2563EB"
                }}
              >
                <FaMicrophone size={22} color="#FFFFFF" />
                <span>{isListening ? "Listening... Speak now!" : "Click & Say Your Name"}</span>
              </button>

              <div style={styles.dividerRow}>
                <span style={styles.dividerText}>or type below</span>
              </div>

              <input
                type="text"
                placeholder="Enter your name..."
                value={inputName}
                onChange={(e) => setInputName(e.target.value)}
                style={styles.modalInput}
              />

              <button
                type="button"
                onClick={() => handleSaveName(inputName)}
                style={styles.modalStartBtn}
              >
                Let's Start Tracing! 🚀
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconCircle}>
              <FaPencilAlt size={20} color="#2563eb" />
            </div>
            <div>
              <h1 style={styles.title}>Cambridge Alphabet Tracing</h1>
              <p style={styles.subtitle}>Learn & Draw Capital Letters A to Z with Audio</p>
            </div>
          </div>

          {/* Quick Name Input Control on Header */}
          <div style={styles.headerRight}>
            <div style={styles.nameInputBox}>
              <FaUser size={14} color="#2563EB" />
              <input
                type="text"
                placeholder="Enter Name"
                value={inputName}
                onChange={(e) => setInputName(e.target.value)}
                style={styles.headerInput}
              />
              <button
                onClick={() => handleSaveName(inputName)}
                style={styles.setNameBtn}
              >
                Set
              </button>
            </div>
          </div>
        </div>

        {/* Alphabet Choice Selector Bar (A to Z) */}
        <div style={styles.numberSelectorBar}>
          <span style={styles.selectorLabel}>Choose a Letter:</span>
          <div style={styles.numberBtnGroup}>
            {alphabetList.map((letter) => (
              <button
                key={letter}
                onClick={() => setSelectedLetter(letter)}
                style={{
                  ...styles.numberChip,
                  backgroundColor: selectedLetter === letter ? "#2563EB" : "#F1F5F9",
                  color: selectedLetter === letter ? "#FFFFFF" : "#334155",
                  borderColor: selectedLetter === letter ? "#1D4ED8" : "#E2E8F0"
                }}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        {/* Speech Banner with Mute Control */}
        <div style={styles.speechBanner}>
          <button
            onClick={toggleMute}
            style={styles.muteBtn}
            title={isMuted ? "Unmute Voice" : "Mute Voice"}
          >
            {isMuted ? (
              <FaVolumeMute size={20} color="#DC2626" />
            ) : (
              <FaVolumeUp size={20} color="#1D4ED8" />
            )}
          </button>
          <p style={{ ...styles.speechText, opacity: isMuted ? 0.6 : 1 }}>
            "{aiSpeech}"
          </p>
        </div>

        {/* Main Content Area: Left Example Box & Right Tracing Canvas */}
        <div style={styles.workspaceContainer}>
          {/* Left Side: Example Word/Emoji Showcase */}
          <div style={styles.exampleBox}>
            <span style={styles.exampleTitle}>Example</span>
            <div style={styles.emojiDisplay}>{currentInfo.exampleEmoji}</div>
            <div style={styles.exampleTextContainer}>
              <span style={styles.exampleHighlight}>{selectedInfoLetterDisplay(selectedLetter)}</span>
              <span style={styles.exampleWordText}>{currentInfo.exampleWord}</span>
            </div>
          </div>

          {/* Canvas Area */}
          <div style={styles.canvasContainer}>
            <canvas
              ref={canvasRef}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              style={styles.canvas}
            />

            {tracingResult && (
              <div style={styles.completionBadge}>
                <FaStar size={16} color="#EAB308" /> {tracingResult.label} ({tracingResult.percent}%)
              </div>
            )}
          </div>
        </div>

        {/* Toolbar & Palette */}
        <div style={styles.toolbar}>
          <div style={styles.colorPalette}>
            <FaPalette size={18} color="#64748B" style={{ marginRight: 6 }} />
            {colors.map((color) => (
              <button
                key={color.hex}
                onClick={() => setSelectedColor(color.hex)}
                style={{
                  ...styles.colorBtn,
                  backgroundColor: color.hex,
                  transform: selectedColor === color.hex ? "scale(1.15)" : "scale(1)",
                  border: selectedColor === color.hex ? "3px solid #0F172A" : "3px solid #FFFFFF"
                }}
                title={color.name}
              />
            ))}
          </div>

          <div style={styles.actionGroup}>
            <div style={styles.sizeSelector}>
              <span style={{ fontSize: "0.8rem", color: "#64748B", fontWeight: "bold" }}>Pencil:</span>
              <button
                onClick={() => setBrushSize(10)}
                style={{ ...styles.sizeBtn, fontWeight: brushSize === 10 ? "bold" : "normal" }}
              >
                Fine
              </button>
              <button
                onClick={() => setBrushSize(18)}
                style={{ ...styles.sizeBtn, fontWeight: brushSize === 18 ? "bold" : "normal" }}
              >
                Thick
              </button>
            </div>

            <button onClick={markComplete} style={styles.doneBtn}>
              <FaCheckCircle size={16} /> Check Tracing
            </button>

            <button onClick={resetCanvas} style={styles.resetBtn}>
              <FaRedo size={14} /> Clear Tracing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function selectedInfoLetterDisplay(letter) {
  return letter;
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#EFF6FF",
    padding: "20px",
    boxSizing: "border-box",
    fontFamily: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif"
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 23, 42, 0.65)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    backdropFilter: "blur(4px)"
  },
  modalCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "24px",
    padding: "32px",
    width: "90%",
    maxWidth: "420px",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
    textAlign: "center",
    border: "4px solid #93C5FD"
  },
  modalHeader: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    marginBottom: "20px"
  },
  modalTitle: {
    margin: 0,
    color: "#1E293B",
    fontSize: "1.5rem",
    fontWeight: "800"
  },
  modalSubtitle: {
    margin: 0,
    color: "#64748B",
    fontSize: "0.95rem"
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    gap: "14px"
  },
  micPromptBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: "#FFFFFF",
    border: "none",
    padding: "14px 20px",
    borderRadius: "14px",
    fontSize: "1rem",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "background-color 0.2s ease"
  },
  dividerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  dividerText: {
    fontSize: "0.8rem",
    color: "#94A3B8",
    fontWeight: "bold",
    textTransform: "uppercase"
  },
  modalInput: {
    padding: "12px 16px",
    borderRadius: "12px",
    border: "2px solid #CBD5E1",
    fontSize: "1rem",
    outline: "none",
    textAlign: "center"
  },
  modalStartBtn: {
    backgroundColor: "#16A34A",
    color: "#FFFFFF",
    border: "none",
    padding: "14px",
    borderRadius: "14px",
    fontSize: "1.05rem",
    fontWeight: "bold",
    cursor: "pointer"
  },
  card: {
    width: "100%",
    maxWidth: "920px",
    backgroundColor: "#FFFFFF",
    borderRadius: "24px",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    border: "4px solid #93C5FD",
    overflow: "hidden"
  },
  header: {
    background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#FFFFFF",
    flexWrap: "wrap",
    gap: "12px"
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  headerRight: {
    display: "flex",
    alignItems: "center"
  },
  nameInputBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#FFFFFF",
    padding: "6px 12px",
    borderRadius: "12px"
  },
  headerInput: {
    border: "none",
    outline: "none",
    fontSize: "0.85rem",
    width: "100px",
    color: "#1E293B",
    fontWeight: "600"
  },
  setNameBtn: {
    backgroundColor: "#2563EB",
    color: "#FFFFFF",
    border: "none",
    padding: "4px 10px",
    borderRadius: "8px",
    fontSize: "0.75rem",
    fontWeight: "bold",
    cursor: "pointer"
  },
  iconCircle: {
    backgroundColor: "#FFFFFF",
    borderRadius: "50%",
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  title: {
    margin: 0,
    fontSize: "1.3rem",
    fontWeight: 800
  },
  subtitle: {
    margin: 0,
    fontSize: "0.85rem",
    opacity: 0.95
  },
  numberSelectorBar: {
    backgroundColor: "#F8FAFC",
    padding: "12px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #E2E8F0",
    flexWrap: "wrap",
    gap: "10px"
  },
  selectorLabel: {
    fontWeight: "bold",
    color: "#475569",
    fontSize: "0.9rem"
  },
  numberBtnGroup: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap"
  },
  numberChip: {
    width: "32px",
    height: "36px",
    borderRadius: "8px",
    fontSize: "0.95rem",
    fontWeight: "bold",
    cursor: "pointer",
    border: "2px solid",
    transition: "all 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  speechBanner: {
    backgroundColor: "#DBEAFE",
    padding: "10px 20px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderBottom: "2px solid #BFDBFE"
  },
  muteBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "4px",
    borderRadius: "6px"
  },
  speechText: {
    margin: 0,
    color: "#1E40AF",
    fontWeight: "600",
    fontStyle: "italic",
    fontSize: "0.95rem"
  },
  workspaceContainer: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    flexWrap: "wrap"
  },
  exampleBox: {
    width: "160px",
    minWidth: "140px",
    backgroundColor: "#F8FAFC",
    borderRight: "2px solid #E2E8F0",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center"
  },
  exampleTitle: {
    fontSize: "0.75rem",
    textTransform: "uppercase",
    fontWeight: "bold",
    color: "#64748B",
    marginBottom: "10px",
    letterSpacing: "0.5px"
  },
  emojiDisplay: {
    fontSize: "3.5rem",
    margin: "10px 0",
    lineHeight: 1
  },
  exampleTextContainer: {
    marginTop: "8px",
    display: "flex",
    flexDirection: "column",
    alignItem: "center"
  },
  exampleHighlight: {
    fontSize: "1.1rem",
    fontWeight: "800",
    color: "#2563EB"
  },
  exampleWordText: {
    fontSize: "0.95rem",
    fontWeight: "700",
    color: "#334155"
  },
  canvasContainer: {
    position: "relative",
    flex: 1,
    minWidth: "280px",
    height: "380px",
    backgroundColor: "#FFFFFF",
    touchAction: "none"
  },
  canvas: {
    width: "100%",
    height: "100%",
    cursor: "crosshair"
  },
  completionBadge: {
    position: "absolute",
    top: "14px",
    right: "14px",
    backgroundColor: "#FEF08A",
    color: "#854D0E",
    padding: "6px 14px",
    borderRadius: "20px",
    fontWeight: "bold",
    fontSize: "0.85rem",
    border: "1px solid #FDE047",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  toolbar: {
    backgroundColor: "#F1F5F9",
    padding: "16px 20px",
    borderTop: "2px solid #E2E8F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px"
  },
  colorPalette: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  colorBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    cursor: "pointer",
    transition: "transform 0.15s ease",
    boxShadow: "0 2px 5px rgba(0,0,0,0.15)"
  },
  actionGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap"
  },
  sizeSelector: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#FFFFFF",
    padding: "4px 8px",
    borderRadius: "10px",
    border: "1px solid #CBD5E1"
  },
  sizeBtn: {
    background: "none",
    border: "none",
    color: "#334155",
    cursor: "pointer",
    fontSize: "0.8rem"
  },
  doneBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#16A34A",
    color: "#FFFFFF",
    border: "none",
    padding: "10px 16px",
    borderRadius: "12px",
    fontWeight: "bold",
    fontSize: "0.9rem",
    cursor: "pointer"
  },
  resetBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#EF4444",
    color: "#FFFFFF",
    border: "none",
    padding: "10px 16px",
    borderRadius: "12px",
    fontWeight: "bold",
    fontSize: "0.9rem",
    cursor: "pointer"
  }
};